import { isMarketplaceProjectVisible } from "../utils/projectVisibility";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Page from "../Components/Page";
import CropYearStatusBadge from "../Components/CropYearStatusBadge.jsx";
import {
  axiosInstance,
  buildLegacyHeaders,
  fetchDashboardSummary,
} from "../Components/axiosFetchers.js";
import { useAuth } from "../Components/Auth/useAuth";
import {
  GrowerSectionHeading,
  GrowerSurface,
  GrowerTableSurface,
  GrowerToolbar,
} from "../Components/GrowerLayout.jsx";
import { trackEvent } from "../analytics/ga4.js";

function unwrapList(payload) {
  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload)) {
    return payload;
  }

  return [];
}

function readValue(record, keys, fallback = null) {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null) {
      return record[key];
    }
  }

  return fallback;
}

function capitalizeToken(token = "") {
  return token
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}

function formatScopeLabel(scope = "") {
  const labels = {
    farmers: "Participant profile",
    farms: "Farms",
    fields: "Fields",
    crop_years: "Crop years",
  };

  return labels[scope] || capitalizeToken(scope);
}

function formatDate(value) {
  if (!value) {
    return "TBA";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "TBA";
  }

  return parsed.toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatDateRange(start, end) {
  if (!start && !end) {
    return "Dates to be announced";
  }

  if (start && end) {
    return `${formatDate(start)} - ${formatDate(end)}`;
  }

  return start ? `Opens ${formatDate(start)}` : `Closes ${formatDate(end)}`;
}

function relativeTimeLabel(targetDate) {
  if (!targetDate) {
    return "Timeline pending";
  }

  const target = new Date(targetDate);

  if (Number.isNaN(target.getTime())) {
    return "Timeline pending";
  }

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const absDays = Math.abs(diffDays);

  if (absDays < 1) {
    return diffDays >= 0 ? "Today" : "Closed today";
  }

  if (absDays < 30) {
    return diffDays >= 0
      ? `${absDays} day${absDays === 1 ? "" : "s"} to go`
      : `${absDays} day${absDays === 1 ? "" : "s"} ago`;
  }

  const months = Math.round(absDays / 30);

  return diffDays >= 0
    ? `${months} month${months === 1 ? "" : "s"} to go`
    : `${months} month${months === 1 ? "" : "s"} ago`;
}

function isCropYearReady(cropYear = {}) {
  return cropYear.workflow_state === "READY" || cropYear.can_run_analysis;
}

function buildProjectTheme(project = {}, index = 0) {
  const palette = [
    {
      accent: "#f15d22",
      tint: "#fff2eb",
      gradient: "from-[#1c2231] via-[#243245] to-[#f15d22]",
    },
    {
      accent: "#00adee",
      tint: "#ebf9ff",
      gradient: "from-[#18283b] via-[#1f4f73] to-[#00adee]",
    },
    {
      accent: "#34a853",
      tint: "#eefaf1",
      gradient: "from-[#1d2c21] via-[#265336] to-[#34a853]",
    },
    {
      accent: "#d78f19",
      tint: "#fff8e8",
      gradient: "from-[#2d2412] via-[#6a4d0f] to-[#d78f19]",
    },
  ];
  const seed =
    `${project.public_id || ""}${project.partner?.name || ""}${project.name || ""}`.length ||
    index;
  const theme = palette[seed % palette.length];
  const initials = `${project.partner?.name || project.name || "P"}`
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  return {
    ...theme,
    initials: initials || "PR",
  };
}

function getProjectLogoUrl(project = {}, size = "small") {
  const smallKeys = [
    project?.partner_logo_small_url,
    project?.partner?.logo_small_url,
    project?.partner?.logoSmallUrl,
  ];
  const largeKeys = [
    project?.partner_logo_large_url,
    project?.partner?.logo_large_url,
    project?.partner?.logoLargeUrl,
  ];
  const fallbackKeys = [
    project?.partner_logo_url,
    project?.partner?.logo_url,
    project?.partner?.logoUrl,
  ];

  const candidates =
    size === "large"
      ? [...largeKeys, ...smallKeys, ...fallbackKeys]
      : [...smallKeys, ...largeKeys, ...fallbackKeys];

  return candidates.find(Boolean) || null;
}

function evaluateCandidateCropYears(project = {}, inventory = []) {
  const criteria = project.criteria || {};
  const provinces = Array.isArray(criteria.provinces)
    ? criteria.provinces.map((value) => String(value).toUpperCase())
    : [];
  const crops = Array.isArray(criteria.crops)
    ? criteria.crops.map((value) => String(value).toLowerCase())
    : [];
  const requireReady = Boolean(criteria.require_analysis_ready);
  const minCropYears = Number(criteria.min_crop_years || 0);

  const provinceMatches = inventory.filter((record) => {
    if (provinces.length === 0) {
      return true;
    }

    return provinces.includes(String(record.province || "").toUpperCase());
  });

  const candidateCropYears = provinceMatches.filter((record) => {
    if (crops.length > 0 && !crops.includes(String(record.crop || "").toLowerCase())) {
      return false;
    }

    if (requireReady && !isCropYearReady(record)) {
      return false;
    }

    return true;
  });

  const reasons = [];

  if (provinces.length > 0 && provinceMatches.length === 0) {
    reasons.push(`No farms in ${provinces.join(", ")}`);
  }

  if (crops.length > 0 && candidateCropYears.length === 0) {
    reasons.push(
      `No matching crop years for ${crops.map((crop) => capitalizeToken(crop)).join(", ")}`
    );
  }

  if (candidateCropYears.length < minCropYears) {
    reasons.push(
      `Need at least ${minCropYears} matching crop year${minCropYears === 1 ? "" : "s"}`
    );
  }

  if (requireReady && candidateCropYears.length === 0) {
    reasons.push("Requires at least one analysis-ready crop year");
  }

  const compatibleFields = new Set(
    candidateCropYears.map((record) => `${record.farmId}:${record.fieldId}`)
  );

  return {
    candidateCropYears,
    compatibleFieldCount: compatibleFields.size,
    compatibleFarmCount: new Set(candidateCropYears.map((record) => record.farmId)).size,
    candidateSummary:
      candidateCropYears.length > 0
        ? `${candidateCropYears.length} compatible crop year${
            candidateCropYears.length === 1 ? "" : "s"
          }`
        : "No compatible crop years yet",
    localReasons: reasons,
  };
}

function mergeProjects(projects, suggestions, enrollments, inventory) {
  const suggestionLookup = new Map(
    suggestions.map((item) => [item.project_id, item])
  );
  const enrollmentById = new Map(enrollments.map((item) => [item.project_id, item]));
  const enrollmentByPublicId = new Map(
    enrollments.map((item) => [item.project_public_id, item])
  );

  return projects.map((project, index) => {
    const compatibility = evaluateCandidateCropYears(project, inventory);
    const theme = buildProjectTheme(project, index);
    const enrollment =
      project.enrollment ||
      enrollmentById.get(project.id) ||
      enrollmentByPublicId.get(project.public_id) ||
      null;
    const eligibility =
      project.eligibility || suggestionLookup.get(project.public_id) || null;

    return {
      ...project,
      theme,
      enrollment,
      eligibility,
      ...compatibility,
    };
  });
}

function buildInventoryRowsFromDashboardSummary(summary = {}) {
  const tableRows = unwrapList(
    readValue(summary, ["table_rows", "tableRows"], [])
  );
  const mapFields = unwrapList(
    readValue(summary, ["map_fields", "mapFields"], [])
  );

  const cropYearLookup = new Map(
    tableRows.map((row) => [
      String(
        readValue(
          readValue(row, ["cropyear"], null),
          ["id"],
          readValue(row, ["cropyear_id", "cropyearId", "id"], "")
        )
      ),
      row,
    ])
  );

  return mapFields.flatMap((field) => {
    const farmRecord = readValue(field, ["farm"], null);
    const farmId = String(
      readValue(farmRecord, ["id"], readValue(field, ["farm_id", "farmId"], ""))
    );
    const fieldId = String(readValue(field, ["id", "field_id", "fieldId"], ""));
    const farmName = readValue(farmRecord, ["name"], readValue(field, ["farm_name", "farmName"], ""));
    const fieldName = readValue(field, ["field_name", "fieldName", "name"], "");
    const province = readValue(farmRecord, ["province"], readValue(field, ["province"], null));
    const cropYears = unwrapList(readValue(field, ["crop_years", "cropYears"], []));

    return cropYears.map((cropYear) => {
      const cropyearId = String(
        readValue(
          readValue(cropYear, ["cropyear"], null),
          ["id"],
          readValue(cropYear, ["cropyear_id", "cropyearId", "id"], "")
        )
      );
      const details = cropYearLookup.get(cropyearId) || {};

      return {
        farmId,
        farmName,
        province,
        fieldId,
        fieldName,
        cropyearId,
        cropYear: readValue(cropYear, ["crop_year", "cropYear"], null),
        crop: readValue(cropYear, ["crop", "crop_this_year", "cropThisYear"], null),
        workflow_state: readValue(details, ["workflow_state"], ""),
        data_status: readValue(details, ["data_status"], ""),
        can_run_analysis: Boolean(
          readValue(details, ["can_run_analysis", "canRunAnalysis"], false)
        ),
        path: `/farm/${farmId}/field/${fieldId}/cropyear/${cropyearId}`,
      };
    });
  });
}

function buildTimelineItems(project) {
  return [
    {
      label: "Applications open",
      value: project.application_open_at,
    },
    {
      label: "Study starts",
      value: project.starts_at,
    },
    {
      label: "Study ends",
      value: project.ends_at,
    },
  ].filter((item) => item.value);
}

function computeProgress(project) {
  const start = project.application_open_at
    ? new Date(project.application_open_at).getTime()
    : null;
  const end = project.ends_at ? new Date(project.ends_at).getTime() : null;
  const now = Date.now();

  if (!start || !end || Number.isNaN(start) || Number.isNaN(end) || start >= end) {
    return 0;
  }

  const progress = ((now - start) / (end - start)) * 100;

  return Math.max(0, Math.min(100, progress));
}

function getEligibilityTone(project) {
  if (project.eligibility?.eligibility_status === "ELIGIBLE") {
    return {
      label: "Eligible",
      className: "bg-green-100 text-green-800 border-green-200",
    };
  }

  if (project.eligibility?.eligibility_status === "INELIGIBLE") {
    if (project.candidateCropYears.length > 0) {
      return {
        label: "Has matches, not eligible yet",
        className: "bg-amber-100 text-amber-900 border-amber-200",
      };
    }

    return {
      label: "Not eligible yet",
      className: "bg-amber-100 text-amber-900 border-amber-200",
    };
  }

  if (project.candidateCropYears.length > 0) {
    return {
      label: "Potential fit",
      className: "bg-sky-100 text-sky-800 border-sky-200",
    };
  }

  return {
    label: "Review project",
    className: "bg-slate-100 text-slate-700 border-slate-200",
  };
}

function getEnrollmentTone(project) {
  if (project.enrollment?.consent_status === "OPTED_IN") {
    return {
      label: "Enrolled",
      className: "bg-[#243245] text-white border-[#243245]",
    };
  }

  if (project.enrollment?.consent_status === "PENDING_APPROVAL") {
    return {
      label: "Approval pending",
      className: "bg-amber-100 text-amber-900 border-amber-200",
    };
  }

  if (project.enrollment?.consent_status === "REJECTED") {
    return {
      label: "Not approved",
      className: "bg-red-50 text-red-800 border-red-200",
    };
  }

  if (project.enrollment?.consent_status === "REVOKED") {
    return {
      label: "Withdrawn",
      className: "bg-slate-100 text-slate-700 border-slate-200",
    };
  }

  return {
    label: "Not enrolled",
    className: "bg-white text-slate-700 border-slate-300",
  };
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M21 21l-4.35-4.35" />
      <circle cx="10.5" cy="10.5" r="6.5" strokeWidth="1.8" />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4.5 w-4.5" fill="none" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
        d="M4 6h16M7 12h10M10 18h4"
      />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}

function SparkTag({ children, className = "" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

function ProjectArt({ project, compact = false }) {
  const theme = project.theme;
  const sizeClass = compact ? "h-[64px] w-[64px]" : "h-[140px] w-[180px]";
  const logoUrl = getProjectLogoUrl(project, compact ? "small" : "large");

  if (logoUrl) {
    return (
      <div
        className={`${sizeClass} relative overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-[0_18px_40px_rgba(24,32,45,0.12)]`}
      >
        <img
          alt={`${project.partner?.name || project.name || "Project"} logo`}
          className="h-full w-full object-contain p-3"
          src={logoUrl}
        />
      </div>
    );
  }

  return (
    <div
      className={`${sizeClass} relative overflow-hidden rounded-[8px] bg-gradient-to-br ${theme.gradient} text-white shadow-[0_18px_40px_rgba(24,32,45,0.18)]`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.35),transparent_42%)]" />
      <div className="absolute -right-6 top-3 h-16 w-16 rounded-full border border-white/20 bg-white/10" />
      <div className="absolute bottom-3 left-3 text-left">
        <div className="text-[11px] uppercase tracking-[0.24em] text-white/70">
          {project.partner?.name || "Project"}
        </div>
        <div className={compact ? "text-xl font-black" : "text-4xl font-black"}>
          {theme.initials}
        </div>
      </div>
    </div>
  );
}

function ProjectStatusRail({ project }) {
  const eligibilityTone = getEligibilityTone(project);
  const enrollmentTone = getEnrollmentTone(project);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <SparkTag className={eligibilityTone.className}>{eligibilityTone.label}</SparkTag>
      <SparkTag className={enrollmentTone.className}>{enrollmentTone.label}</SparkTag>
      <SparkTag className="border-slate-200 bg-white text-slate-600">
        {project.candidateSummary}
      </SparkTag>
    </div>
  );
}

function ProjectListRow({ project, onOpen }) {
  const eligibilityTone = getEligibilityTone(project);
  const dataScope = Array.isArray(project.data_scope) ? project.data_scope : [];

  return (
    <button
      className="group flex w-full items-stretch gap-4 border-b border-slate-200 px-4 py-5 text-left transition hover:bg-white"
      onClick={onOpen}
      type="button"
    >
      <div className="shrink-0">
        <ProjectArt compact project={project} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
          <span>{project.partner?.name || "Partner Project"}</span>
          <span>&bull;</span>
          <span>{relativeTimeLabel(project.ends_at)}</span>
        </div>
        <h2 className="mt-2 text-[28px] font-black leading-tight text-slate-900 group-hover:text-[#243245]">
          {project.name}
        </h2>
        <p className="mt-2 line-clamp-2 max-w-[860px] text-[15px] leading-7 text-slate-600">
          {project.criteria_summary || project.research_purpose || "Project details coming soon."}
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] text-slate-500">
          <span>{project.compatibleFieldCount} matching fields</span>
          <span>&bull;</span>
          <span>{formatDateRange(project.application_open_at, project.ends_at)}</span>
          {dataScope.length > 0 ? (
            <>
              <span>&bull;</span>
              <span>{dataScope.map(formatScopeLabel).join(", ")}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="hidden w-[220px] shrink-0 items-end justify-between gap-3 md:flex md:flex-col">
        <div className="w-full text-right">
          <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Fit
          </div>
          <div className="mt-2 inline-flex rounded-full border px-3 py-1 text-sm font-semibold text-slate-700">
            {eligibilityTone.label}
          </div>
        </div>
        <div className="w-full text-right">
          <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-400">
            Reward
          </div>
          <p className="mt-2 line-clamp-3 text-[13px] font-semibold leading-5 text-slate-800">
            {project.reward_summary || "See details"}
          </p>
        </div>
        <div className="inline-flex items-center gap-2 self-end text-sm font-semibold text-[#243245]">
          View project
          <ArrowRightIcon />
        </div>
      </div>
    </button>
  );
}

function EmptyState({ title, description }) {
  return (
    <div className="rounded-[8px] border border-dashed border-slate-300 bg-white px-8 py-16 text-center">
      <h2 className="text-2xl font-black text-slate-900">{title}</h2>
      <p className="mx-auto mt-3 max-w-[620px] text-[15px] leading-7 text-slate-600">
        {description}
      </p>
    </div>
  );
}

function CandidateCropYearCard({ cropYear }) {
  return (
    <Link
      className="block rounded-[8px] border border-slate-200 bg-white p-4 transition hover:-translate-y-[1px] hover:border-slate-300 hover:shadow-[0_16px_30px_rgba(20,28,38,0.08)]"
      to={cropYear.path}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-400">
            {cropYear.farmName}
          </div>
          <h3 className="mt-1 text-lg font-bold text-slate-900">{cropYear.fieldName}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {cropYear.cropYear || "Draft"} • {capitalizeToken(cropYear.crop || "unassigned")}
          </p>
        </div>
        <CropYearStatusBadge cropYear={cropYear} />
      </div>
      <div className="mt-4 text-sm text-slate-600">
        {cropYear.province || "Province pending"}
      </div>
    </Link>
  );
}

function ProjectsListView({
  projects,
  searchValue,
  onSearchChange,
  selectedFilter,
  onFilterChange,
  sortMode,
  onSortChange,
  onOpenProject,
}) {
  const filterPills = [
    { id: "all", label: "All projects" },
    { id: "eligible", label: "Eligible now" },
    { id: "enrolled", label: "Enrolled" },
    { id: "pending", label: "Pending approval" },
    { id: "matching", label: "Has matching crop years" },
  ];

  return (
    <div className="w-full text-[#666666]">
      <GrowerSurface className="w-full">
        <GrowerSectionHeading
          actions={
            <div className="rounded-[8px] border border-slate-200 bg-white px-5 py-4 text-right shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
              <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Projects live
              </div>
              <div className="mt-2 text-4xl font-black text-slate-900">{projects.length}</div>
            </div>
          }
          eyebrow="Project Marketplace"
          title="Browse live grower projects"
          description="Find research programs that match your farm footprint, see which crop years already fit, and jump into a detail view before you enroll."
        />
        <GrowerToolbar className="mt-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3 rounded-full border border-slate-200 bg-white px-5 py-3 text-slate-500 lg:min-w-[420px]">
              <SearchIcon />
              <input
                className="w-full bg-transparent text-[16px] text-slate-900 outline-none placeholder:text-slate-400"
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder="Search projects, partners, provinces, or crops"
                value={searchValue}
              />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                <FilterIcon />
                Filters
              </div>
              <select
                className="rounded-full border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 outline-none"
                onChange={(event) => onSortChange(event.target.value)}
                value={sortMode}
              >
                <option value="recent">Recently opened</option>
                <option value="closing">Ending soon</option>
                <option value="compatible">Most compatible</option>
                <option value="reward">Reward / incentive</option>
              </select>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            {filterPills.map((filter) => (
              <button
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition ${
                  selectedFilter === filter.id
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                }`}
                key={filter.id}
                onClick={() => onFilterChange(filter.id)}
                type="button"
              >
                {filter.label}
              </button>
            ))}
          </div>
        </GrowerToolbar>
      </GrowerSurface>

      <GrowerTableSurface
        className="mt-6 w-full"
        meta={`${projects.length} project${projects.length === 1 ? "" : "s"} shown`}
        title="Results"
      >
        <div className="overflow-hidden rounded-[8px] border border-slate-200 bg-[#fcfcfa] shadow-[0_18px_45px_rgba(15,23,42,0.05)]">
          {projects.length === 0 ? (
            <div className="p-8">
              <EmptyState
                description="Try widening the filters or clearing the search to see more live projects."
                title="No projects match this view"
              />
            </div>
          ) : (
            projects.map((project) => (
              <ProjectListRow
                key={project.public_id}
                onOpen={() => onOpenProject(project.public_id)}
                project={project}
              />
            ))
          )}
        </div>
      </GrowerTableSurface>
    </div>
  );
}

function scrollToSection(e, id) {
  e.preventDefault();
  const el = document.getElementById(id);
  const scroller = document.getElementById("scrollableDiv");
  if (!el) return;
  if (scroller) {
    const offsetTop = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTo({ top: offsetTop - 24, behavior: "smooth" });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function TocLink({ target, children }) {
  return (
    <a
      className="block text-[15px] text-slate-500 transition hover:text-slate-900"
      href={`#${target}`}
      onClick={(e) => scrollToSection(e, target)}
    >
      {children}
    </a>
  );
}

function ProjectDetailView({
  project,
  actionState,
  consentAcknowledged,
  onConsentAcknowledgedChange,
  onEnroll,
  onWithdraw,
}) {
  const isEnrolled = project.enrollment?.consent_status === "OPTED_IN";
  const isPending = project.enrollment?.consent_status === "PENDING_APPROVAL";
  const isRejected = project.enrollment?.consent_status === "REJECTED";
  const enrollmentClosed =
    project.ends_at && new Date(project.ends_at).getTime() < Date.now();
  const isEligible = project.eligibility?.eligibility_status === "ELIGIBLE";
  const progress = computeProgress(project);
  const timelineItems = buildTimelineItems(project);
  const dataScope = Array.isArray(project.data_scope) ? project.data_scope : [];
  const acceptedAt =
    project.enrollment?.consent_snapshot?.accepted_at ||
    project.enrollment?.consented_at ||
    null;

  return (
    <div className="min-h-full w-full bg-[#fbfbf8]">
      <div className="mx-auto w-full max-w-[1240px]">
        <div className="border-x border-b border-slate-200 bg-white">
          {/* Hero header */}
          <section className="border-b border-slate-200 bg-white px-6 py-7 md:px-10 md:py-9">
            <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
              {/* Left: title + actions */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3 text-[12px] font-bold uppercase tracking-[0.2em] text-slate-500">
                  <Link className="text-[#243245] hover:text-[#f15d22]" to="/projects">
                    Projects
                  </Link>
                  <span>&bull;</span>
                  <span>{project.partner?.name || "Research partner"}</span>
                  <span>&bull;</span>
                  <span>{relativeTimeLabel(project.ends_at)}</span>
                </div>
                <h1 className="mt-5 text-[34px] font-black leading-tight text-slate-900 md:text-[44px]">
                  {project.name}
                </h1>
                <p className="mt-4 text-[17px] leading-8 text-slate-600">
                  {project.research_purpose ||
                    project.criteria_summary ||
                    "Project overview details will appear here once provided."}
                </p>
                <div className="mt-6">
                  <ProjectStatusRail project={project} />
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-3">
                  {isEnrolled || isPending ? (
                    <button
                      className="rounded-[4px] bg-slate-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      disabled={actionState.loading}
                      onClick={onWithdraw}
                      type="button"
                    >
                      {actionState.loading
                        ? "Updating..."
                        : isPending
                          ? "Cancel request"
                          : "Withdraw"}
                    </button>
                  ) : (
                    <button
                      className="rounded-[4px] bg-slate-900 px-6 py-3 text-sm font-bold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                      disabled={
                        actionState.loading ||
                        enrollmentClosed ||
                        !isEligible ||
                        !consentAcknowledged
                      }
                      onClick={onEnroll}
                      type="button"
                    >
                      {actionState.loading
                        ? "Submitting..."
                        : project.requires_admin_approval !== false
                          ? "Request to join"
                          : "Join project"}
                    </button>
                  )}
                  <Link
                    className="rounded-[4px] border border-slate-300 px-6 py-3 text-sm font-bold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                    to="/projects"
                  >
                    Back to list
                  </Link>
                </div>
                {!isEnrolled && !isPending && isEligible && !enrollmentClosed ? (
                  <label className="mt-4 flex max-w-[720px] items-start gap-3 rounded-[6px] border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-700">
                    <input
                      aria-label="Acknowledge project consent"
                      checked={consentAcknowledged}
                      className="mt-1 h-4 w-4"
                      onChange={(event) =>
                        onConsentAcknowledgedChange(event.target.checked)
                      }
                      type="checkbox"
                    />
                    <span>
                      {project.requires_admin_approval !== false
                        ? "I consent to share the listed project data if an administrator approves my enrollment, and I acknowledge the reward terms."
                        : "I consent to share the listed project data while I am enrolled, and I acknowledge the reward terms."}
                    </span>
                  </label>
                ) : null}
                {isPending ? (
                  <div className="mt-4 border-l-4 border-amber-500 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                    Your request is waiting for Field to Market Canada approval. Project data is not shared with the partner until approval.
                  </div>
                ) : null}
                {isRejected ? (
                  <div className="mt-4 border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800">
                    Your previous request was not approved
                    {project.enrollment?.review_notes ? `: ${project.enrollment.review_notes}` : ". You may submit a new request if your circumstances have changed."}
                  </div>
                ) : null}
                {actionState.message ? (
                  <div
                    className={`mt-5 rounded-[8px] border px-4 py-3 text-sm ${
                      actionState.type === "error"
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-green-200 bg-green-50 text-green-700"
                    }`}
                  >
                    {actionState.message}
                  </div>
                ) : null}
              </div>

              {/* Right: large logo */}
              <div className="shrink-0">
                <ProjectArt project={project} />
              </div>
            </div>
          </section>

          {/* Body: content + sticky TOC */}
          <section className="grid gap-8 px-6 py-8 md:px-10 lg:grid-cols-[minmax(0,1fr)_200px]">
            <div className="space-y-8">
              <section id="overview">
                <h2 className="text-[22px] font-black text-slate-900">Overview</h2>
                <p className="mt-4 text-[16px] leading-8 text-slate-700">
                  {project.criteria_summary ||
                    "Eligibility and participation details will appear here as this project evolves."}
                </p>
                <p className="mt-4 text-[16px] leading-8 text-slate-700">
                  {project.research_purpose ||
                    "Use this project workspace to understand whether your current farm footprint already contains compatible crop years before you commit to enrollment."}
                </p>
              </section>

              <section id="timeline" className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-6">
                <h2 className="text-[22px] font-black text-slate-900">Timeline</h2>
                <div className="mt-6">
                  <div className="flex items-center justify-between text-sm font-semibold text-slate-600">
                    <span>Applications open</span>
                    <span>Study closes</span>
                  </div>
                  <div className="mt-4 h-[6px] rounded-full bg-slate-200">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#00adee] via-[#243245] to-[#34a853]"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="mt-4 grid gap-4 md:grid-cols-3">
                    {timelineItems.map((item) => (
                      <div key={item.label}>
                        <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                          {item.label}
                        </div>
                        <div className="mt-1 text-lg font-bold text-slate-900">
                          {formatDate(item.value)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              <section id="compatibility">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <h2 className="text-[22px] font-black text-slate-900">Compatible crop years</h2>
                    <p className="mt-2 text-[15px] leading-7 text-slate-600">
                      These crop years match the current project criteria based on province,
                      crop history, and readiness rules.
                    </p>
                  </div>
                  <SparkTag className="border-slate-200 bg-white text-slate-700">
                    {project.candidateSummary}
                  </SparkTag>
                </div>
                {project.candidateCropYears.length === 0 ? (
                  <div className="mt-5">
                    <EmptyState
                      description="This project does not line up with any current crop years yet. Check eligibility criteria below."
                      title="No compatible crop years found"
                    />
                  </div>
                ) : (
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    {project.candidateCropYears.map((cropYear) => (
                      <CandidateCropYearCard
                        cropYear={cropYear}
                        key={`${cropYear.fieldId}-${cropYear.cropyearId}`}
                      />
                    ))}
                  </div>
                )}
              </section>

              <section id="data-access" className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <h2 className="text-[22px] font-black text-slate-900">Data access</h2>
                    <p className="mt-3 max-w-[760px] text-[15px] leading-7 text-slate-600">
                      {project.requires_admin_approval !== false
                        ? "Data is shared only after administrator approval and only for the project scope below while your consent is active."
                        : "Data is shared after you join and only for the project scope below while your consent is active."}
                    </p>
                  </div>
                  {isEnrolled ? (
                    <SparkTag className="border-green-200 bg-green-50 text-green-800">
                      Accepted {formatDate(acceptedAt)}
                    </SparkTag>
                  ) : null}
                  {isPending ? (
                    <SparkTag className="border-amber-200 bg-amber-50 text-amber-900">
                      Approval pending
                    </SparkTag>
                  ) : null}
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {dataScope.length > 0 ? (
                    dataScope.map((scope) => (
                      <SparkTag className="border-slate-200 bg-white text-slate-700" key={scope}>
                        {formatScopeLabel(scope)}
                      </SparkTag>
                    ))
                  ) : (
                    <SparkTag className="border-slate-200 bg-white text-slate-500">
                      Scope pending
                    </SparkTag>
                  )}
                </div>
                {isEnrolled && project.enrollment?.consent_snapshot?.project ? (
                  <div className="mt-5 grid gap-4 md:grid-cols-2">
                    <div className="rounded-[6px] border border-slate-200 bg-white p-4">
                      <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                        Accepted project copy
                      </div>
                      <div className="mt-2 text-[15px] font-semibold leading-6 text-slate-800">
                        {project.enrollment.consent_snapshot.project.criteria_summary ||
                          project.enrollment.consent_snapshot.project.research_purpose ||
                          "Project terms captured"}
                      </div>
                    </div>
                    <div className="rounded-[6px] border border-slate-200 bg-white p-4">
                      <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                        Reward acknowledgement
                      </div>
                      <div className="mt-2 text-[15px] font-semibold leading-6 text-slate-800">
                        {project.enrollment.consent_snapshot.reward_acknowledged
                          ? "Acknowledged"
                          : "Not acknowledged"}
                      </div>
                    </div>
                  </div>
                ) : null}
              </section>

              <section id="rewards" className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-6">
                <h2 className="text-[22px] font-black text-slate-900">Rewards</h2>
                <p className="mt-4 text-[16px] leading-8 text-slate-700">
                  {project.reward_summary ||
                    "Reward details are not yet published for this project."}
                </p>
              </section>

              <section id="rules" className="rounded-[8px] border border-slate-200 bg-[#fcfcfa] p-6">
                <h2 className="text-[22px] font-black text-slate-900">Rules and criteria</h2>
                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <div className="rounded-[6px] border border-slate-200 bg-white p-4">
                    <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Provinces
                    </div>
                    <div className="mt-2 text-[15px] font-semibold text-slate-800">
                      {(project.criteria?.provinces || []).join(", ") || "Any province"}
                    </div>
                  </div>
                  <div className="rounded-[6px] border border-slate-200 bg-white p-4">
                    <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Crops
                    </div>
                    <div className="mt-2 text-[15px] font-semibold text-slate-800">
                      {(project.criteria?.crops || []).map(capitalizeToken).join(", ") ||
                        "Any crop"}
                    </div>
                  </div>
                  <div className="rounded-[6px] border border-slate-200 bg-white p-4">
                    <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Minimum crop years
                    </div>
                    <div className="mt-2 text-[15px] font-semibold text-slate-800">
                      {project.criteria?.min_crop_years || 0}
                    </div>
                  </div>
                  <div className="rounded-[6px] border border-slate-200 bg-white p-4">
                    <div className="text-[12px] font-bold uppercase tracking-[0.18em] text-slate-400">
                      Analysis ready required
                    </div>
                    <div className="mt-2 text-[15px] font-semibold text-slate-800">
                      {project.criteria?.require_analysis_ready ? "Yes" : "No"}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            {/* Sticky table of contents */}
            <aside className="self-start">
              <div className="sticky top-6 rounded-[6px] border border-slate-200 bg-white p-5">
                <h3 className="text-[13px] font-bold uppercase tracking-[0.18em] text-slate-400">
                  On this page
                </h3>
                <nav className="mt-4 flex flex-col gap-3">
                  <TocLink target="overview">Overview</TocLink>
                  <TocLink target="timeline">Timeline</TocLink>
                  <TocLink target="compatibility">Compatibility</TocLink>
                  <TocLink target="data-access">Data access</TocLink>
                  <TocLink target="rewards">Rewards</TocLink>
                  <TocLink target="rules">Rules</TocLink>
                </nav>
              </div>
            </aside>
          </section>
        </div>
      </div>
    </div>
  );
}

export default function ProjectsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { projectPublicId } = useParams();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [sortMode, setSortMode] = useState("recent");
  const [actionState, setActionState] = useState({
    loading: false,
    type: "",
    message: "",
  });
  const [consentAcknowledged, setConsentAcknowledged] = useState(false);

  useEffect(() => {
    document.title = "Projects - Field To Market Canada";
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function loadProjects() {
      setIsLoading(true);
      setPageError("");

      try {
        const projectsRequest = user?.token
          ? axiosInstance.get(`${process.env.REACT_APP_API_URL}/projects/summary`, {
              headers: buildLegacyHeaders(user),
            })
          : axiosInstance.get(`${process.env.REACT_APP_API_URL}/projects`);
        const dashboardSummaryRequest = user?.token
          ? fetchDashboardSummary(user)
          : Promise.resolve({});

        const [projectsResponse, dashboardSummary] = await Promise.all([
          projectsRequest,
          dashboardSummaryRequest,
        ]);

        const projectRows = unwrapList(projectsResponse.data);
        const inventory = buildInventoryRowsFromDashboardSummary(dashboardSummary);
        const mergedProjects = mergeProjects(projectRows, [], [], inventory);

        if (!isMounted) {
          return;
        }

        setProjects(mergedProjects.filter(isMarketplaceProjectVisible));
      } catch (error) {
        console.error("Failed to load projects", error);

        if (!isMounted) {
          return;
        }

        setPageError("We couldn't load the project marketplace right now.");
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadProjects();

    return () => {
      isMounted = false;
    };
  }, [user]);

  const visibleProjects = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    const filtered = projects.filter((project) => {
      if (selectedFilter === "eligible") {
        if (project.eligibility?.eligibility_status !== "ELIGIBLE") {
          return false;
        }
      }

      if (selectedFilter === "enrolled") {
        if (project.enrollment?.consent_status !== "OPTED_IN") {
          return false;
        }
      }

      if (selectedFilter === "pending") {
        if (project.enrollment?.consent_status !== "PENDING_APPROVAL") {
          return false;
        }
      }

      if (selectedFilter === "matching") {
        if (project.candidateCropYears.length === 0) {
          return false;
        }
      }

      if (!query) {
        return true;
      }

      const haystack = [
        project.name,
        project.partner?.name,
        project.criteria_summary,
        project.research_purpose,
        project.reward_summary,
        ...(project.criteria?.provinces || []),
        ...(project.criteria?.crops || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(query);
    });

    return [...filtered].sort((left, right) => {
      if (sortMode === "closing") {
        return new Date(left.ends_at || 0).getTime() - new Date(right.ends_at || 0).getTime();
      }

      if (sortMode === "compatible") {
        return right.candidateCropYears.length - left.candidateCropYears.length;
      }

      if (sortMode === "reward") {
        return String(right.reward_summary || "").localeCompare(String(left.reward_summary || ""));
      }

      return (
        new Date(right.application_open_at || right.created_at || 0).getTime() -
        new Date(left.application_open_at || left.created_at || 0).getTime()
      );
    });
  }, [projects, searchValue, selectedFilter, sortMode]);

  const selectedProject = useMemo(
    () => projects.find((project) => project.public_id === projectPublicId) || null,
    [projectPublicId, projects]
  );

  async function handleEnroll(project) {
    if (!user?.token) {
      setActionState({
        loading: false,
        type: "error",
        message: "Please sign in again before enrolling in a project.",
      });
      return;
    }

    setActionState({
      loading: true,
      type: "",
      message: "",
    });

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_API_URL}/project-enrollments/${project.public_id}`,
        { reward_acknowledged: consentAcknowledged },
        {
          headers: buildLegacyHeaders(user),
        }
      );
      const enrollment = response.data?.data || response.data;

      setProjects((currentProjects) =>
        currentProjects.map((currentProject) =>
          currentProject.public_id === project.public_id
            ? {
                ...currentProject,
                enrollment,
              }
            : currentProject
        )
      );
      trackEvent("project_enrollment_submitted", {
        workflow_area: "projects",
        result: "success",
      });
      setActionState({
        loading: false,
        type: "success",
        message:
          enrollment?.consent_status === "OPTED_IN"
            ? "You joined the project successfully."
            : "Your enrollment request was sent for administrator approval.",
      });
      setConsentAcknowledged(false);
    } catch (error) {
      const errorBody = error?.response?.data || {};
      const reasonLines = Array.isArray(errorBody.reasons)
        ? errorBody.reasons.join(" ")
        : Array.isArray(errorBody.eligibility_reasons)
          ? errorBody.eligibility_reasons.join(" ")
          : "";

      setActionState({
        loading: false,
        type: "error",
        message:
          [errorBody.message || "Enrollment could not be completed.", reasonLines]
            .filter(Boolean)
            .join(" "),
      });
    }
  }

  async function handleWithdraw(project) {
    if (!user?.token) {
      setActionState({
        loading: false,
        type: "error",
        message: "Please sign in again before updating your enrollment.",
      });
      return;
    }

    setActionState({
      loading: true,
      type: "",
      message: "",
    });

    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_URL}/project-enrollments/${project.public_id}`,
        {
          headers: buildLegacyHeaders(user),
        }
      );

      setProjects((currentProjects) =>
        currentProjects.map((currentProject) =>
          currentProject.public_id === project.public_id
            ? {
                ...currentProject,
                enrollment: currentProject.enrollment
                  ? {
                      ...currentProject.enrollment,
                      consent_status: "REVOKED",
                    }
                  : {
                      project_id: currentProject.id,
                      consent_status: "REVOKED",
                    },
              }
            : currentProject
        )
      );
      const wasPending = project.enrollment?.consent_status === "PENDING_APPROVAL";
      setActionState({
        loading: false,
        type: "success",
        message: wasPending
          ? "Your enrollment request was cancelled."
          : "You have been withdrawn from this project.",
      });
    } catch (error) {
      const errorBody = error?.response?.data || {};

      setActionState({
        loading: false,
        type: "error",
        message: errorBody.message || "We couldn't update your enrollment right now.",
      });
    }
  }

  useEffect(() => {
    setActionState({
      loading: false,
      type: "",
      message: "",
    });
    setConsentAcknowledged(false);
  }, [projectPublicId]);

  return (
    <Page
      title={projectPublicId && selectedProject ? selectedProject.name : "Projects"}
      headerBorderColor="border-[#f15d22]"
      headerVariant="hybrid"
      contentWidth="wide"
      padding={projectPublicId ? "p-0" : "px-4 pb-6 pt-5 md:px-6 md:pb-8"}
      showQuickFacts={false}
      introContent={
        projectPublicId && selectedProject ? (
          <p>
            {selectedProject.criteria_summary ||
              selectedProject.research_purpose ||
              "Review project eligibility, matching crop years, and participation details before you enroll."}
          </p>
        ) : (
          <p>
            Browse research programs that match your farm footprint, see which crop years are eligible, and check the details.  
          </p>
        )
      }
    >
      {isLoading ? (
        <div className="flex min-h-[400px] w-full items-center justify-center bg-[#fbfbf8] text-lg text-slate-500">
          Loading projects...
        </div>
      ) : pageError ? (
        <div className="flex w-full bg-[#fbfbf8] p-8">
          <EmptyState
            description="Please refresh the page in a moment. If this keeps happening, the API might be unavailable."
            title={pageError}
          />
        </div>
      ) : projectPublicId ? (
        <div key={projectPublicId} className="page-enter w-full">
          {selectedProject ? (
            <ProjectDetailView
              actionState={actionState}
              consentAcknowledged={consentAcknowledged}
              onConsentAcknowledgedChange={setConsentAcknowledged}
              onEnroll={() => handleEnroll(selectedProject)}
              onWithdraw={() => handleWithdraw(selectedProject)}
              project={selectedProject}
            />
          ) : (
            <div className="flex w-full bg-[#fbfbf8] p-8">
              <EmptyState
                description="The project may have been removed, or the link might be out of date."
                title="Project not found"
              />
            </div>
          )}
        </div>
      ) : (
        <div key="list" className="page-enter w-full">
          <ProjectsListView
            onFilterChange={setSelectedFilter}
            onOpenProject={(publicId) => navigate(`/projects/${publicId}`)}
            onSearchChange={setSearchValue}
            onSortChange={setSortMode}
            projects={visibleProjects}
            searchValue={searchValue}
            selectedFilter={selectedFilter}
            sortMode={sortMode}
          />
        </div>
      )}
    </Page>
  );
}
